import {
  type KillChainCategory,
  StudyAdversaryProfile,
  type StudyListSelectDto,
  type StudyListSelectDtoStatusEnum,
  StudyTimeHorizon,
} from '@@types/api.types';
import { canPersonaCreateStudies } from '@azymmetric/shared/access/intelligence-persona.types';
import { MainLayout } from '@components/layouts/main.layout';
import { BlueLabel } from '@components/shared/blue-label.component';
import { ClassificationBadge } from '@components/shared/classification-badge.component';
import { EmptyStateContainer } from '@components/shared/empty-state-container.component';
import { HelmetWrapper } from '@components/shared/helmet.component';
import { PageviewTracker } from '@components/shared/pageview-tracker.component';
import { StudyStatusBadge } from '@components/shared/study-status-badge.component';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { ClearableSearchInput } from '@components/ui/clearable-search-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { Skeleton } from '@components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/table';
import { cn } from '@lib/utils';
import { useGetMeiaStudies } from '@queries/meia.query';
import { useGetCurrentUser } from '@queries/users.query';
import { routes } from '@routes';
import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { canAccessMeiaStudioV1 } from '@utils/meia-access.util';
import {
  getStudyAdversaryProfileLabel,
  getStudyThreadKillChainLabel,
  getStudyTimeHorizonLabel,
} from '@utils/meia-enum.util';
import { ArrowDownWideNarrowIcon, ArrowUpDown, ArrowUpNarrowWideIcon, XIcon } from 'lucide-react';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { MeiaStudyCreateModal } from './components/meia-study-create-modal.component';
import {
  DEFAULT_MEIA_STUDIES_FILTERS,
  type MeiaStudiesFilters,
  STUDY_PATTERN_OPTIONS,
  STUDY_STATUS_OPTIONS,
  filterMeiaStudies,
  toggleArrayMember,
} from './meia-studies-filter.util';
import { meiaRoute } from './meia.constants';

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const ADVERSARY_PROFILE_OPTIONS = Object.values(StudyAdversaryProfile);
const TIME_HORIZON_OPTIONS = Object.values(StudyTimeHorizon);

const STATUS_ALL_VALUE = 'all';
const PATTERN_ALL_VALUE = 'all';

const SKELETON_COLUMNS = 7;
const SKELETON_ROWS = 4;

function formatDate(value: string): string {
  return dateFormatter.format(new Date(value));
}

function getStudyPath(studyId: string): string {
  return meiaRoute.studyOverview(studyId);
}

type MeiaStudiesTableProps = {
  emptyMessage: string;
  isLoading: boolean;
  studies: StudyListSelectDto[];
};

function MeiaStudiesSkeletonRows(): JSX.Element {
  return (
    <>
      {Array.from({ length: SKELETON_ROWS }).map((_, rowIndex) => (
        <TableRow key={`study-skeleton-${rowIndex}`}>
          {Array.from({ length: SKELETON_COLUMNS }).map((__, cellIndex) => (
            <TableCell key={`study-skeleton-${rowIndex}-${cellIndex}`}>
              <Skeleton className="h-5 w-32" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function MeiaStudyAdversaryHorizonCell(props: { study: StudyListSelectDto }): JSX.Element {
  const { study } = props;
  const timeHorizonLabel = getStudyTimeHorizonLabel(study.timeHorizon);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap gap-1">
        {study.adversaryProfiles.length === 0 ? (
          <span className="text-sm text-gray-400">—</span>
        ) : (
          study.adversaryProfiles.map((profile) => (
            <Badge
              key={profile}
              size="xs"
              variant="primary"
            >
              {getStudyAdversaryProfileLabel(profile)}
            </Badge>
          ))
        )}
      </div>
      {timeHorizonLabel && (
        <Badge
          className="w-fit"
          size="xs"
          variant="default"
        >
          {timeHorizonLabel}
        </Badge>
      )}
    </div>
  );
}

function MeiaStudyPatternsCell(props: { killChains: KillChainCategory[] }): JSX.Element {
  const { killChains } = props;

  if (killChains.length === 0) {
    return <span className="text-sm text-gray-400">—</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {killChains.map((killChain) => (
        <Badge
          key={killChain}
          size="xs"
          variant="default"
        >
          {getStudyThreadKillChainLabel(killChain)}
        </Badge>
      ))}
    </div>
  );
}

function MeiaStudiesTable(props: MeiaStudiesTableProps): JSX.Element {
  const { emptyMessage, isLoading, studies } = props;
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([{ desc: true, id: 'updatedAt' }]);

  const columns = useMemo<ColumnDef<StudyListSelectDto>[]>(
    () => [
      {
        accessorKey: 'name',
        cell: ({ row }) => <span className="text-sm font-semibold text-gray-950">{row.original.name}</span>,
        header: 'Name',
        size: 320,
      },
      {
        accessorKey: 'classificationLevel',
        cell: ({ row }) => <ClassificationBadge classification={row.original.classificationLevel} />,
        header: 'Class',
        size: 140,
      },
      {
        accessorKey: 'status',
        cell: ({ row }) => <StudyStatusBadge status={row.original.status} />,
        header: 'Status',
        size: 140,
      },
      {
        accessorKey: 'adversaryProfiles',
        cell: ({ row }) => <MeiaStudyAdversaryHorizonCell study={row.original} />,
        enableSorting: false,
        header: 'Adv · Horizon',
        size: 220,
      },
      {
        accessorKey: 'threadKillChains',
        cell: ({ row }) => <MeiaStudyPatternsCell killChains={row.original.threadKillChains} />,
        enableSorting: false,
        header: 'Patterns',
        size: 200,
      },
      {
        accessorKey: 'ownerName',
        cell: ({ row }) => <span className="text-sm text-gray-600">{row.original.ownerName || '—'}</span>,
        header: 'Author',
        size: 160,
      },
      {
        accessorKey: 'updatedAt',
        cell: ({ row }) => <span className="text-sm text-gray-600">{formatDate(row.original.updatedAt)}</span>,
        header: 'Updated',
        size: 140,
      },
    ],
    [],
  );

  const table = useReactTable({
    columns,
    data: studies,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  return (
    <div className="overflow-hidden rounded-xl border bg-white">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                if (header.isPlaceholder) {
                  return <TableHead key={header.id} />;
                }

                const canSort = header.column.getCanSort();
                const sorted = header.column.getIsSorted();

                return (
                  <TableHead
                    className="text-xs font-semibold text-gray-500"
                    key={header.id}
                    style={{ width: header.getSize() }}
                  >
                    {canSort ? (
                      <button
                        aria-label={`Sort by ${
                          typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : 'column'
                        }`}
                        aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : 'none'}
                        className="flex items-center gap-1 text-xs font-semibold text-gray-600"
                        onClick={header.column.getToggleSortingHandler()}
                        type="button"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {sorted === 'asc' && <ArrowUpNarrowWideIcon className="size-3.5" />}
                        {sorted === 'desc' && <ArrowDownWideNarrowIcon className="size-3.5" />}
                        {!sorted && <ArrowUpDown className="size-3.5 text-gray-400" />}
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isLoading && <MeiaStudiesSkeletonRows />}

          {!isLoading &&
            table.getRowModel().rows.map((row) => (
              <TableRow
                className="cursor-pointer hover:bg-gray-50"
                key={row.id}
                onClick={() => navigate(getStudyPath(row.original.id))}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    style={{ width: cell.column.getSize() }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}

          {!isLoading && studies.length === 0 && (
            <TableRow>
              <TableCell
                className="h-24 text-center text-sm text-gray-500"
                colSpan={SKELETON_COLUMNS}
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

type MeiaStudiesFilterChipsProps = {
  filters: MeiaStudiesFilters;
  onChange: (next: MeiaStudiesFilters) => void;
};

function MeiaStudiesFilterChips(props: MeiaStudiesFilterChipsProps): JSX.Element {
  const { filters, onChange } = props;
  const hasChipFilters = filters.adversaryProfiles.length > 0 || filters.timeHorizons.length > 0;

  const toggleAdversaryProfile = (profile: StudyAdversaryProfile): void => {
    onChange({ ...filters, adversaryProfiles: toggleArrayMember(filters.adversaryProfiles, profile) });
  };

  const toggleTimeHorizon = (horizon: StudyTimeHorizon): void => {
    onChange({ ...filters, timeHorizons: toggleArrayMember(filters.timeHorizons, horizon) });
  };

  const clearChips = (): void => {
    onChange({ ...filters, adversaryProfiles: [], timeHorizons: [] });
  };

  return (
    <div className="flex flex-col gap-3 rounded-md border border-gray-200 bg-gray-50/60 p-3">
      <div className="flex flex-col gap-1.5">
        <BlueLabel
          className="text-xs"
          text="Adversary"
        />
        <div className="flex flex-wrap items-center gap-2">
          {ADVERSARY_PROFILE_OPTIONS.map((profile) => {
            const isActive = filters.adversaryProfiles.includes(profile);

            return (
              <button
                aria-pressed={isActive}
                className={cn(
                  'rounded-sm border px-2 py-0.5 text-[11px] transition-colors',
                  isActive
                    ? 'border-primary/40 bg-primary/10 text-primary'
                    : 'border-gray-200 text-gray-500 hover:border-gray-400',
                )}
                key={profile}
                onClick={() => toggleAdversaryProfile(profile)}
                type="button"
              >
                {getStudyAdversaryProfileLabel(profile)}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <BlueLabel
          className="text-xs"
          text="Time horizon"
        />
        <div className="flex flex-wrap items-center gap-2">
          {TIME_HORIZON_OPTIONS.map((horizon) => {
            const isActive = filters.timeHorizons.includes(horizon);

            return (
              <button
                aria-pressed={isActive}
                className={cn(
                  'rounded-sm border px-2 py-0.5 text-[11px] transition-colors',
                  isActive
                    ? 'border-primary/40 bg-primary/10 text-primary'
                    : 'border-gray-200 text-gray-500 hover:border-gray-400',
                )}
                key={horizon}
                onClick={() => toggleTimeHorizon(horizon)}
                type="button"
              >
                {getStudyTimeHorizonLabel(horizon)}
              </button>
            );
          })}
        </div>
      </div>
      <Button
        className="self-end gap-1"
        disabled={!hasChipFilters}
        onClick={clearChips}
        size="xs"
        variant="outline"
      >
        <XIcon className="size-3" />
        Clear Filters
      </Button>
    </div>
  );
}

export const MeiaStudiesPage: FC = () => {
  const { data: user, isPending: isUserPending } = useGetCurrentUser();
  const { data: studies = [], error, isError, isLoading } = useGetMeiaStudies();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<MeiaStudiesFilters>(DEFAULT_MEIA_STUDIES_FILTERS);
  const isCreateModalOpen = searchParams.get('newStudy') === 'true';
  const isPageLoading = isLoading || isUserPending;

  const filteredStudies = useMemo(() => filterMeiaStudies(studies, filters), [studies, filters]);
  const canCreateStudies = canPersonaCreateStudies(user?.selectedCompanyPersona);
  const hasActiveFilters =
    filters.search.trim().length > 0 ||
    filters.status !== 'all' ||
    filters.pattern !== 'all' ||
    filters.adversaryProfiles.length > 0 ||
    filters.timeHorizons.length > 0;
  const shouldShowNoStudiesEmptyState = !isPageLoading && studies.length === 0;
  const shouldShowFilterEmptyState = hasActiveFilters && studies.length > 0 && filteredStudies.length === 0;

  if (!isUserPending && !canAccessMeiaStudioV1(user)) {
    return <Navigate to={routes.home.path} />;
  }

  const openCreateModal = (): void => {
    setSearchParams((params) => {
      const nextParams = new URLSearchParams(params);
      nextParams.set('newStudy', 'true');
      return nextParams;
    });
  };

  const closeCreateModal = (): void => {
    setSearchParams((params) => {
      const nextParams = new URLSearchParams(params);
      nextParams.delete('newStudy');
      return nextParams;
    });
  };

  const updateSearch = (value: string | null): void => {
    setFilters((current) => ({ ...current, search: value ?? '' }));
  };

  const updateStatus = (value: string): void => {
    setFilters((current) => ({
      ...current,
      status: value === STATUS_ALL_VALUE ? 'all' : (value as StudyListSelectDtoStatusEnum),
    }));
  };

  const updatePattern = (value: string): void => {
    setFilters((current) => ({
      ...current,
      pattern: value === PATTERN_ALL_VALUE ? 'all' : (value as KillChainCategory),
    }));
  };

  return (
    <PageviewTracker>
      <HelmetWrapper title="MEIA Studio" />
      <MainLayout fullWidth>
        <div className="mx-auto flex w-full max-w-(--breakpoint-lg) flex-col gap-4">
          {isError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
              {error.errors[0]?.message ?? 'Unable to load MEIA studies.'}
            </div>
          )}

          {!shouldShowNoStudiesEmptyState && (
            <Tile
              contentClassName="gap-3"
              header={{
                button: canCreateStudies ? { onClick: openCreateModal, text: '+ Add Study' } : undefined,
                title: 'Studies',
              }}
            >
              <div className="flex flex-wrap items-center gap-2">
                <ClearableSearchInput
                  className="max-w-xs"
                  onChange={updateSearch}
                  placeholder="Filter studies by name…"
                  value={filters.search}
                />
                <Select
                  onValueChange={updateStatus}
                  value={filters.status === 'all' ? STATUS_ALL_VALUE : filters.status}
                >
                  <SelectTrigger
                    aria-label="Filter studies by status"
                    className="h-10 w-44"
                  >
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={STATUS_ALL_VALUE}>All statuses</SelectItem>
                    {STUDY_STATUS_OPTIONS.map((status) => (
                      <SelectItem
                        key={status}
                        value={status}
                      >
                        <StudyStatusBadge
                          showIcon={false}
                          status={status}
                        />
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  onValueChange={updatePattern}
                  value={filters.pattern === 'all' ? PATTERN_ALL_VALUE : filters.pattern}
                >
                  <SelectTrigger
                    aria-label="Filter studies by pattern"
                    className="h-10 w-44"
                  >
                    <SelectValue placeholder="All patterns" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={PATTERN_ALL_VALUE}>All patterns</SelectItem>
                    {STUDY_PATTERN_OPTIONS.map((pattern) => (
                      <SelectItem
                        key={pattern}
                        value={pattern}
                      >
                        {getStudyThreadKillChainLabel(pattern)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <MeiaStudiesFilterChips
                filters={filters}
                onChange={setFilters}
              />
            </Tile>
          )}

          {shouldShowNoStudiesEmptyState ? (
            <div className="min-h-56 overflow-hidden rounded-xl border bg-white">
              <EmptyStateContainer
                className="min-h-56"
                message={canCreateStudies ? '+ Add Study' : undefined}
                onClick={canCreateStudies ? openCreateModal : undefined}
                title="No Studies"
              />
            </div>
          ) : (
            <MeiaStudiesTable
              emptyMessage={shouldShowFilterEmptyState ? 'No studies match your filter.' : ''}
              isLoading={isPageLoading}
              studies={filteredStudies}
            />
          )}
        </div>
      </MainLayout>
      <MeiaStudyCreateModal
        isOpen={canCreateStudies && isCreateModalOpen}
        onClose={closeCreateModal}
      />
    </PageviewTracker>
  );
};
